// Sample-tube label printer.
//
// Renders one or more labels in a visible preview window with a Print
// button. Keeping the preview visible (rather than auto-printing from a
// hidden iframe) means that if the Brady i5300 feeds a blank label the
// tech can immediately tell whether the app produced content (and the
// printer dropped it) or whether the app itself failed.
//
// Barcode is Code128B (1D linear barcode) so the clinic's existing 1D
// scanner can read it. Encoded payload is the lab number (the unique
// identifier for each blood-draw request) — scanning the tube returns
// the lab number, which the system uses to look up patient + request.
// The patient ID and name are still printed as human-readable text so
// a tech can match a tube to a patient by eye even without a scanner.

// ─── Configuration ────────────────────────────────────────────────────
// Default chosen as the most common tube-label size. Click "Size" in
// the preview toolbar to switch — the choice is persisted to
// localStorage under "labelSize" so this only needs to be done once
// per workstation.
const DEFAULT_WIDTH_MM  = 50;
const DEFAULT_HEIGHT_MM = 25;
const PREVIEW_SCALE     = 4;   // on-screen pixels per label-mm

function resolveSize(opts) {
  if (opts && opts.widthMm && opts.heightMm) {
    return { widthMm: Number(opts.widthMm), heightMm: Number(opts.heightMm) };
  }
  try {
    const saved = window.localStorage.getItem('labelSize');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && parsed.widthMm && parsed.heightMm) {
        return { widthMm: Number(parsed.widthMm), heightMm: Number(parsed.heightMm) };
      }
    }
  } catch (_) { /* ignore parse errors */ }
  return { widthMm: DEFAULT_WIDTH_MM, heightMm: DEFAULT_HEIGHT_MM };
}

// ─── Inline Code128B encoder ──────────────────────────────────────────
// Self-contained so the label still prints on a clinic LAN with no
// internet. CODE128_PATTERNS[i] is the 11-module pattern (7 modules for
// the stop code) encoded as alternating bar/space width digits.
//   even index → bar (drawn black)
//   odd index  → space (skipped)
//   widths sum to 11 (or 13 for the stop code).
//
// Index 0-95   → printable ASCII (subtract 32 from the char code)
// Index 104    → Start Code B
// Index 106    → Stop pattern
const CODE128_PATTERNS = [
  '212222','222122','222221','121223','121322','131222','122213','122312',
  '132212','221213','221312','231212','112232','122132','122231','113222',
  '123122','123221','223211','221132','221231','213212','223112','312131',
  '311222','321122','321221','312212','322112','322211','212123','212321',
  '232121','111323','131123','131321','112313','132113','132311','211313',
  '231113','231311','112133','112331','132131','113123','113321','133121',
  '313121','211331','231131','213113','213311','213131','311123','311321',
  '331121','312113','312311','332111','314111','221411','431111','111224',
  '111422','121124','121421','141122','141221','112214','112412','122114',
  '122411','142112','142211','241211','221114','413111','241112','134111',
  '111242','121142','121241','114212','124112','124211','411212','421112',
  '421211','212141','214121','412121','111143','111341','131141','114113',
  '114311','411113','411311','113141','114131','311141','411131','211412',
  '211214','211232','2331112',  // 104 = Start B, 106 = Stop
];
const START_B = 104;
const STOP    = 106;

function encodeCode128B(text) {
  const values = [START_B];
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c < 32 || c > 126) return null;
    values.push(c - 32);
  }
  // Weighted mod-103 checksum.
  let sum = values[0];
  for (let i = 1; i < values.length; i++) sum += i * values[i];
  values.push(sum % 103);
  values.push(STOP);

  let x = 0;
  const bars = [];
  for (const v of values) {
    const pattern = CODE128_PATTERNS[v];
    for (let i = 0; i < pattern.length; i++) {
      const w = parseInt(pattern[i], 10);
      if (i % 2 === 0) bars.push({ x, w });
      x += w;
    }
  }
  return { width: x, bars };
}

function buildBarcodeSVG(text, viewWidthMm, heightMm) {
  const encoded = encodeCode128B(text);
  if (!encoded) return '';
  const moduleWidth = viewWidthMm / encoded.width;
  let rects = '';
  for (const b of encoded.bars) {
    rects += `<rect x="${(b.x * moduleWidth).toFixed(3)}" y="0" width="${(b.w * moduleWidth).toFixed(3)}" height="${heightMm}" fill="#000"/>`;
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${viewWidthMm}mm" height="${heightMm}mm" viewBox="0 0 ${viewWidthMm} ${heightMm}" shape-rendering="crispEdges">${rects}</svg>`;
}

// ─── HTML rendering ───────────────────────────────────────────────────
const escapeHTML = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const renderLabel = (label, widthMm, heightMm) => {
  const pidRaw = String(label.patientId || '');
  const pid    = escapeHTML(pidRaw);
  // Minimal label — Code128 of the patient ID only, no other fields.
  // Barcode fills most of the label so the scanner has the widest /
  // tallest target possible; a small human-readable copy of the ID
  // sits underneath for visual fallback.
  const barcodeWidthMm  = widthMm - 3;
  const barcodeHeightMm = Math.max(6, heightMm * 0.7);
  const barcodeSVG = pidRaw ? buildBarcodeSVG(pidRaw, barcodeWidthMm, barcodeHeightMm) : '';

  return `
    <div class="label">
      <div class="barcode-wrap">${barcodeSVG}</div>
      <div class="ln id">${pid}</div>
    </div>
  `;
};

const buildHTML = (labels, widthMm, heightMm) => `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Sample Labels — Preview</title>
  <style>
    @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }

    html, body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; background: #eee; color: #000; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

    .toolbar {
      position: sticky; top: 0;
      background: #11998e; color: #fff;
      padding: 10px 14px; display: flex; gap: 8px; align-items: center;
      box-shadow: 0 2px 6px rgba(0,0,0,0.15);
    }
    .toolbar h1 { font-size: 14px; margin: 0; flex: 1; font-weight: 600; }
    .toolbar button { padding: 7px 14px; font-size: 13px; border: 0; border-radius: 4px; cursor: pointer; font-weight: 600; }
    .toolbar .primary   { background: #fff; color: #11998e; }
    .toolbar .secondary { background: rgba(255,255,255,0.18); color: #fff; }
    .hint { font-size: 12px; padding: 8px 14px; background: #fff8e1; color: #6b5d20; border-bottom: 1px solid #f0e2a0; }

    .preview-wrap { padding: 24px; display: flex; flex-direction: column; align-items: center; gap: 16px; }
    .label {
      width: ${widthMm}mm;
      height: ${heightMm}mm;
      box-sizing: border-box;
      padding: 1mm 1.5mm;
      background: #fff;
      color: #000;
      overflow: hidden;
      box-shadow: 0 1px 4px rgba(0,0,0,0.2);
      page-break-after: always;
      /* Centre everything; barcode is the focal point, ID label sits
         tight beneath it. */
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      gap: 0.5mm;
    }
    .label:last-child { page-break-after: auto; }
    .ln.id { font-size: 9pt; font-weight: 700; letter-spacing: 0.5px; line-height: 1; margin: 0; text-align: center; }
    .barcode-wrap { width: 100%; display: flex; justify-content: center; }
    .barcode-wrap svg { display: block; }

    @media screen {
      .preview-wrap .label {
        transform: scale(${PREVIEW_SCALE});
        transform-origin: top left;
        margin-bottom: ${heightMm * PREVIEW_SCALE - heightMm + 16}px;
        margin-right:  ${widthMm  * PREVIEW_SCALE - widthMm}px;
      }
    }
    @media print {
      body { background: #fff; }
      .toolbar, .hint { display: none !important; }
      .preview-wrap { padding: 0; gap: 0; }
      .label { box-shadow: none; transform: none !important; margin: 0 !important; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <h1>Sample Labels (${labels.length}) — ${widthMm} × ${heightMm} mm</h1>
    <label style="color:#fff;font-size:12px;">Size:&nbsp;
      <select id="sizePicker" onchange="pickSize(this.value)"
              style="padding:5px 8px;border-radius:4px;border:0;font-weight:600;">
        <option value="">— choose —</option>
        <option value="25.4x12.7">25.4 × 12.7 mm (1" × 0.5") — tiny tube</option>
        <option value="38x19">38 × 19 mm (1.5" × 0.75") — small tube</option>
        <option value="50x25">50 × 25 mm (2" × 1") — standard tube</option>
        <option value="51x19">51 × 19 mm (2" × 0.75") — wrap-around</option>
        <option value="63.5x25.4">63.5 × 25.4 mm (2.5" × 1") — wide tube</option>
        <option value="76x25">76 × 25 mm (3" × 1") — driver default</option>
        <option value="76x38">76 × 38 mm (3" × 1.5") — large</option>
        <option value="100x50">100 × 50 mm (4" × 2") — full sheet</option>
        <option value="custom">Custom… (enter mm)</option>
      </select>
    </label>
    <button class="secondary" onclick="window.close()">Close</button>
    <button class="primary"   onclick="window.print()">Print</button>
  </div>
  <div class="hint">
    Brady i5300 setup: Settings → Printers → Brady i5300 → Printing Preferences →
    set <b>Label Size</b> to <b>${widthMm} × ${heightMm} mm</b> (or pick a different
    Size above to match what's loaded). In Chrome's print dialog choose
    <b>Margins: None</b>, <b>Scale: 100</b>, and uncheck <b>Headers and footers</b>.
  </div>
  <div class="preview-wrap">
    ${labels.map((l) => renderLabel(l, widthMm, heightMm)).join('')}
  </div>
  <script>
    function pickSize(value) {
      if (!value) return;
      var w, h;
      if (value === 'custom') {
        var wIn = prompt('Label WIDTH in mm:', '${widthMm}');
        if (!wIn) return;
        var hIn = prompt('Label HEIGHT in mm:', '${heightMm}');
        if (!hIn) return;
        w = Number(wIn); h = Number(hIn);
      } else {
        var parts = value.split('x');
        w = Number(parts[0]);
        h = Number(parts[1]);
      }
      if (!w || !h) return;
      try {
        window.opener && window.opener.localStorage.setItem(
          'labelSize',
          JSON.stringify({ widthMm: w, heightMm: h })
        );
      } catch (e) {}
      try {
        window.opener && window.opener.__rePrintSampleLabels &&
          window.opener.__rePrintSampleLabels();
        window.close();
      } catch (e) {
        alert('Saved ' + w + ' x ' + h + ' mm. Close this window and click Print Label again.');
      }
    }
  </script>
</body>
</html>`;

// ─── Public API ───────────────────────────────────────────────────────
//
//   printSampleLabels([
//     { patientName, patientId, date, testName, sampleId },
//     ...
//   ], { widthMm, heightMm })
export function printSampleLabels(labels, opts) {
  if (!Array.isArray(labels) || labels.length === 0) return;

  // Remember the last set of labels so the preview's Size dropdown can
  // re-open with new dimensions without the tech clicking "Print Label"
  // a second time.
  window.__lastSampleLabels = labels;
  window.__rePrintSampleLabels = () => printSampleLabels(window.__lastSampleLabels || []);

  const { widthMm, heightMm } = resolveSize(opts);
  const html = buildHTML(labels, widthMm, heightMm);

  const winW = Math.max(560, widthMm * PREVIEW_SCALE + 80);
  const win = window.open('', '_blank', `width=${winW},height=720,scrollbars=yes`);
  if (!win) {
    alert('Pop-up blocked. Allow pop-ups for this site, then click Print Label again.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
}

export default printSampleLabels;
